/**
 * useDatesEffect — Effect 1 of the /map state machine.
 *
 * Spec: docs/superpowers/specs/2026-05-07-map-state-machine.md §7.2 + §3.4
 * Plan: docs/superpowers/plans/2026-05-07-sprint-2026-21-map-refactor.md Task 4
 *
 * Responsibility:
 *   When `(activeScope, activeRasterMode)` changes AND a Product is derivable
 *   from the raster mode, fetch /api/v1/satellite/dates and populate
 *   `availableDates` for the (scope_type, scope_id, product) cache key.
 *
 *   On a successful fetch (or cache hit), if `selectedDate` is currently
 *   null, auto-select the latest available date — the *last* element of the
 *   ASC-sorted array (ISO date strings sort lexicographically = chronological).
 *
 * Founder lock-ins (do not relax without ADR — spec §13):
 *   #1  selectedDate is preserved across product switches. We auto-select
 *       only when selectedDate === null. If the user already picked a date
 *       and then switches NDVI → NDVI_CONTRAST, the date stays as-is even
 *       though we may refetch the dates list for the new product.
 *
 * Reducer surface (T2):
 *   The current reducer ships `DATES_FETCH_SUCCESS` only. There are no
 *   `DATES_FETCH_START` / `_ERROR` actions — the dates fetch is best-effort
 *   and silent on failure. If we need an error surface later we'll add it
 *   to the action union together with reducer state. For now we swallow
 *   non-abort errors (logged to console for diagnostics; not surfaced via
 *   coverageStatus, which is reserved for /coverage lifecycle).
 *
 * Cleanup:
 *   AbortController on every fetch — unmounting the component or a
 *   dependency change before the request lands cancels it. AbortError is
 *   distinguished from real errors and ignored.
 */
import { useEffect } from 'react';
import { useMapStore } from '../useMapStoreV2';
import { productOf } from '../derived/productOf';
import { availableKey } from '../derived/availableKey';
import { chunkDateRange } from '../derived/chunkDateRange';
import type { AvailableKey, RegionWarmStatus, RegionWarmStatusKind } from '../state/mapState';
import { useActiveSeason } from '../../season/SeasonContext';

interface DatesResponse {
  dates: Array<{ date: string; covered_count: number }>;
  // AK-604-follow: only present when `dates` is empty — see
  // backend/src/ikolhoz/services/region_warm_status.py.
  region_status?: RegionWarmStatusKind;
  eta_minutes?: number;
}

/** Backend → store shape. `undefined` when the response carried no status
 * (dates non-empty, or an older backend). */
function toRegionWarmStatus(body: DatesResponse): RegionWarmStatus | undefined {
  if (!body.region_status) return undefined;
  return body.eta_minutes != null
    ? { kind: body.region_status, etaMinutes: body.eta_minutes }
    : { kind: body.region_status };
}

/**
 * In-flight fetch guard. Module-scoped — survives React StrictMode's
 * double-invocation of effects in dev, rapid re-renders during dispatch
 * cascades, and parallel mounts of the hook in tests. Once a key resolves
 * it is removed; the result lands in `availableDates` and subsequent calls
 * take the cache-hit branch.
 */
const inFlight = new Set<AvailableKey>();

export function useDatesEffect(): void {
  // Per-field selectors avoid the new-object-every-render trap of
  // useMapStore() — each subscription fires only when its slice changes.
  const activeScope = useMapStore((s) => s.activeScope);
  const rasterMode = useMapStore((s) => s.activeRasterMode);
  const selectedDate = useMapStore((s) => s.selectedDate);
  const availableDates = useMapStore((s) => s.availableDates);
  const dispatch = useMapStore((s) => s.dispatch);

  // AK-195: the active season drives the dates window. `useActiveSeason`
  // returns `{ season: null }` outside a SeasonProvider, in which case we keep
  // the legacy default-window behaviour.
  const { season } = useActiveSeason();
  const seasonId = season?.seasonId ?? null;
  const seasonStart = season?.startsOn ?? null;
  const seasonEnd = season?.endsOn ?? null;

  useEffect(() => {
    const product = productOf(rasterMode);
    if (!product) return;

    // AK-195: season is part of the cache key so a year switch refetches and
    // caches per season instead of reusing the previous year's dates.
    const key: AvailableKey = availableKey(
      activeScope.type,
      activeScope.id,
      product,
      seasonId,
    );

    // Cache hit: skip network, but still honour auto-select policy if
    // selectedDate happens to be null.
    const cached = availableDates.get(key);
    if (cached) {
      if (!selectedDate && cached.size > 0) {
        const sorted = Array.from(cached).sort();
        dispatch({ type: 'SET_SELECTED_DATE', date: sorted[sorted.length - 1] });
      }
      return;
    }

    // Dedup concurrent requests for the same key (StrictMode, rapid re-renders).
    if (inFlight.has(key)) return;
    inFlight.add(key);

    const ac = new AbortController();

    interface WindowResult {
      dates: string[];
      // AK-604-follow: only the backend response for THIS window's own empty
      // result carries a status — merged below.
      status?: RegionWarmStatus;
    }

    const fetchWindow = (from?: string, to?: string): Promise<WindowResult> => {
      const params = new URLSearchParams({ scope_type: activeScope.type, product });
      if (activeScope.id) params.set('scope_id', activeScope.id);
      if (from && to) {
        params.set('from', from);
        params.set('to', to);
      }
      return fetch(`/api/v1/satellite/dates?${params.toString()}`, {
        signal: ac.signal,
        credentials: 'include',
      })
        .then((r) => {
          if (!r.ok) throw new Error(`dates ${r.status}`);
          return r.json() as Promise<DatesResponse>;
        })
        .then((body) => ({ dates: body.dates.map((d) => d.date), status: toRegionWarmStatus(body) }));
    };

    // AK-195: with an active season, fetch the full [startsOn, endsOn] range in
    // <=90-day chunks (backend NFR-6 hard cap — a single year-long request
    // would 422) and merge. Without a season, fall back to the backend's
    // default 60-day window (one request, no from/to).
    const windows =
      seasonStart && seasonEnd ? chunkDateRange(seasonStart, seasonEnd) : null;

    const work: Promise<WindowResult> =
      windows && windows.length > 0
        ? Promise.allSettled(windows.map((w) => fetchWindow(w.from, w.to))).then(
            (results) => {
              const merged = new Set<string>();
              let anyFulfilled = false;
              // Windows are chronological (chunkDateRange, ascending) — the
              // LAST chunk with a status is the most recent window, and thus
              // the most relevant "why is nothing recent here" answer.
              let status: RegionWarmStatus | undefined;
              for (const res of results) {
                if (res.status === 'fulfilled') {
                  anyFulfilled = true;
                  for (const d of res.value.dates) merged.add(d);
                  if (res.value.status) status = res.value.status;
                }
              }
              if (!anyFulfilled) throw new Error('all date chunks failed');
              return { dates: Array.from(merged), status: merged.size === 0 ? status : undefined };
            },
          )
        : fetchWindow();

    work
      .then(({ dates: datesArr, status }) => {
        // Reducer expects Set<string> (mapState.MapAction.DATES_FETCH_SUCCESS).
        dispatch({ type: 'DATES_FETCH_SUCCESS', key, dates: new Set(datesArr), status });

        // Founder lock-in #1: auto-select latest ONLY when selectedDate was
        // null at the time the effect ran (captured closure value).
        if (!selectedDate && datesArr.length > 0) {
          const sorted = datesArr.slice().sort();
          dispatch({ type: 'SET_SELECTED_DATE', date: sorted[sorted.length - 1] });
        }
      })
      .catch((err: unknown) => {
        // Abort (unmount / dep change) — including an all-chunks-aborted merge
        // failure — is expected; swallow.
        if (ac.signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;
        // Best-effort logging; no error surface in reducer yet (see header).
        // eslint-disable-next-line no-console
        console.warn('[useDatesEffect] fetch failed:', err);
      })
      .finally(() => {
        // Cleanup aborts the in-flight fetch but does NOT clear the inFlight
        // guard; .finally() clears it once the promise settles. Critical under
        // StrictMode where mount-cleanup-mount fires synchronously.
        inFlight.delete(key);
      });

    return () => ac.abort();
  }, [
    activeScope.type,
    activeScope.id,
    rasterMode,
    selectedDate,
    availableDates,
    dispatch,
    seasonId,
    seasonStart,
    seasonEnd,
  ]);
}
